(() => {
class PtcHero {
  constructor(element) {
    this.element = element;
    this.slides = [...element.querySelectorAll('[data-hero-slide]')];
    this.dots = [...element.querySelectorAll('[data-hero-dot]')];
    this.autoplayToggle = element.querySelector('[data-hero-autoplay-toggle]');
    this.index = 0;
    this.interval = Number(element.dataset.interval) || 6000;
    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.autoplay = element.dataset.autoplay === 'true' && !this.reducedMotion;
    this.userPaused = false;
    this.transition = element.dataset.transition || 'fade';
    this.transitionDuration = Math.min(1000, Math.max(300, Number(element.dataset.transitionDuration) || 600));
    this.animationToken = 0;
    element.querySelector('[data-hero-prev]')?.addEventListener('click', () => this.show(this.index - 1, true));
    element.querySelector('[data-hero-next]')?.addEventListener('click', () => this.show(this.index + 1, true));
    this.dots.forEach((dot) => dot.addEventListener('click', () => this.show(Number(dot.dataset.index), true)));
    if (this.autoplayToggle) {
      this.autoplayToggle.hidden = !this.autoplay;
      this.autoplayToggle.addEventListener('click', () => {
        this.userPaused = !this.userPaused;
        this.autoplayToggle.setAttribute('aria-pressed', String(this.userPaused));
        this.autoplayToggle.setAttribute('aria-label', this.userPaused ? 'Reanudar rotación automática' : 'Pausar rotación automática');
        const symbol = this.autoplayToggle.querySelector('[aria-hidden="true"]');
        if (symbol) symbol.textContent = this.userPaused ? '▶' : 'Ⅱ';
        if (this.userPaused) this.stop(); else this.start();
      });
    }
    element.addEventListener('mouseenter', () => this.stop());
    element.addEventListener('mouseleave', () => this.start());
    element.addEventListener('focusin', () => this.stop());
    element.addEventListener('focusout', () => this.start());
    this.bindSwipe();
    document.addEventListener('shopify:block:select', (event) => {
      const slide = event.target.closest('[data-hero-slide]');
      if (slide && element.contains(slide)) this.show(Number(slide.dataset.index), true);
    });
    this.start();
  }

  bindSwipe() {
    const track = this.element.querySelector('.ptc-banner-stage__slides, .ptc-editorial-hero__slides');
    if (!track || this.slides.length < 2) return;

    let startX = 0;
    let startY = 0;
    let pointerId = null;
    let suppressClick = false;

    track.addEventListener('pointerdown', (event) => {
      if (event.pointerType === 'mouse') return;
      pointerId = event.pointerId;
      startX = event.clientX;
      startY = event.clientY;
      this.stop();
    });

    track.addEventListener('pointerup', (event) => {
      if (event.pointerId !== pointerId) return;

      const deltaX = event.clientX - startX;
      const deltaY = event.clientY - startY;
      const isHorizontalSwipe = Math.abs(deltaX) >= 42 && Math.abs(deltaX) > Math.abs(deltaY) * 1.15;

      pointerId = null;
      if (isHorizontalSwipe) {
        suppressClick = true;
        this.show(this.index + (deltaX < 0 ? 1 : -1), true);
        window.setTimeout(() => { suppressClick = false; }, 450);
      } else {
        this.start();
      }
    });

    track.addEventListener('pointercancel', (event) => {
      if (event.pointerId !== pointerId) return;
      pointerId = null;
      this.start();
    });

    track.addEventListener('click', (event) => {
      if (!suppressClick) return;
      event.preventDefault();
      event.stopPropagation();
      suppressClick = false;
    }, true);
  }

  show(index, restart = false) {
    if (this.slides.length < 2) return;
    const previousIndex = this.index;
    const nextIndex = (index + this.slides.length) % this.slides.length;
    if (nextIndex === previousIndex) {
      if (restart) { this.stop(); this.start(); }
      return;
    }
    const direction = index > previousIndex ? 1 : -1;
    this.index = nextIndex;
    this.animateTransition(previousIndex, nextIndex, direction);
    this.dots.forEach((dot, dotIndex) => dot.setAttribute('aria-selected', String(dotIndex === this.index)));
    if (restart) { this.stop(); this.start(); }
  }

  animateTransition(previousIndex, nextIndex, direction) {
    const previous = this.slides[previousIndex];
    const next = this.slides[nextIndex];
    const token = ++this.animationToken;

    this.slides.forEach((slide, slideIndex) => {
      slide.getAnimations?.().forEach((animation) => animation.cancel());
      slide.style.removeProperty('z-index');
      slide.hidden = slideIndex !== previousIndex && slideIndex !== nextIndex;
    });
    next.hidden = false;

    if (this.reducedMotion || this.transition === 'none' || typeof next.animate !== 'function') {
      previous.hidden = true;
      return;
    }

    previous.style.zIndex = '1';
    next.style.zIndex = '2';
    const distance = direction > 0 ? '3%' : '-3%';
    const reverseDistance = direction > 0 ? '-2%' : '2%';
    const frames = {
      fade: {
        incoming: [{ opacity: 0 }, { opacity: 1 }],
        outgoing: [{ opacity: 1 }, { opacity: 0 }]
      },
      slide: {
        incoming: [{ opacity: .25, transform: `translateX(${distance})` }, { opacity: 1, transform: 'translateX(0)' }],
        outgoing: [{ opacity: 1, transform: 'translateX(0)' }, { opacity: 0, transform: `translateX(${reverseDistance})` }]
      },
      zoom: {
        incoming: [{ opacity: 0, transform: 'scale(1.025)' }, { opacity: 1, transform: 'scale(1)' }],
        outgoing: [{ opacity: 1, transform: 'scale(1)' }, { opacity: 0, transform: 'scale(.99)' }]
      },
      reveal: {
        incoming: [{ opacity: .65, clipPath: direction > 0 ? 'inset(0 0 0 6%)' : 'inset(0 6% 0 0)' }, { opacity: 1, clipPath: 'inset(0)' }],
        outgoing: [{ opacity: 1 }, { opacity: .65 }]
      }
    };
    const selectedFrames = frames[this.transition] || frames.fade;
    const options = { duration: this.transitionDuration, easing: 'cubic-bezier(.22, .61, .36, 1)', fill: 'both' };
    const incomingAnimation = next.animate(selectedFrames.incoming, options);
    previous.animate(selectedFrames.outgoing, options);

    incomingAnimation.finished.then(() => {
      if (token !== this.animationToken) return;
      this.slides.forEach((slide, slideIndex) => {
        slide.hidden = slideIndex !== nextIndex;
        slide.style.removeProperty('z-index');
        slide.getAnimations?.().forEach((animation) => animation.cancel());
      });
    }).catch(() => {});
  }

  start() {
    if (!this.autoplay || this.userPaused || this.slides.length < 2 || this.timer) return;
    this.timer = window.setInterval(() => this.show(this.index + 1), this.interval);
  }

  stop() {
    window.clearInterval(this.timer);
    this.timer = null;
  }
}

document.querySelectorAll('[data-hero]').forEach((element) => {
  if (element.dataset.heroReady === 'true') return;
  element.dataset.heroReady = 'true';
  new PtcHero(element);
});
})();
